import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
    useRef,
} from "react";
import { Base } from "../../framework/base";
import {
    getVehiclePositions,
    getVehicles,
} from "../../services/vehicleService";
import { useFetch } from "../../hooks/useFetch";
import { useInterval } from "usehooks-ts";
import { WorkShiftDto } from "../../models/workShift/workShift";
import {
    getCurrentWorkShift,
    getWorkShiftsNew,
} from "../../services/workShiftService";
import {
    TransportOrderListItemDto,
    TransportOrderState,
} from "../../models/transport/transportOrder";
import { getTransportOrders } from "../../services/transportOrderService";
import { handleApiError } from "../../models/store/storeEffects";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { useReload } from "../../hooks";
import { MonitorViewMainLayout } from "./monitorViewMainLayout";
import { fetchEmployees as getEmployees } from "../../store/employeeSlice";
import {
    setSelectedWorkShift,
    setVehicles,
    setPositions,
} from "../../store/monitorViewSlice";
import {
    fetchWorkTimeTypes,
    setWorkTimeTypeIds,
} from "../../store/monitorViewFiltersSlice";
import {
    fetchAllVehicleGroups,
    fetchAllVehicleCameras,
} from "../../store/vehicleSlice";

const refreshInterval = 60 * 1000; // 1 minute

export const MonitorViewMain = () => {
    const dispatch = useAppDispatch();

    const [positionsLoading, setPositionsLoading] = useState(true);
    const [workShiftLoading, setWorkShiftLoading] = useState(false);
    const [workShiftsLoading, setWorkShiftsLoading] = useState(true);
    const workTimeTypesLoading = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypesLoading
    );
    const loading =
        positionsLoading ||
        workShiftLoading ||
        workShiftsLoading ||
        workTimeTypesLoading;
    const [key, reload] = useReload();

    const workTimeTypes = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypes
    );
    const hasInitializedFilters = useRef(false);

    const [ongoingWorkShifts, setOngoingWorkShifts] = useState<WorkShiftDto[]>(
        []
    );
    const [transportOrders, setTransportOrders] = useState<
        TransportOrderListItemDto[]
    >([]);

    const employees = useAppSelector((state) => state.employees.employees);

    const {
        selectedVehicleId,
        selectedEmployeeId,
        selectedWorkShift,
        vehicles,
        positions,
    } = useAppSelector((state) => state.monitorView);

    const { allVehicleGroups, vehicleCameras } = useAppSelector(
        (state) => state.vehicle
    );

    const visibleEmployeeIds = useMemo(() => {
        return employees?.map((e) => e.id) ?? [];
    }, [employees]);

    // Fetch workshift and timeslots for each employee
    const fetchOngoingWorkShifts = useCallback(
        (showLoadingIndicator = false) => {
            if (visibleEmployeeIds.length === 0) return;

            if (showLoadingIndicator) {
                setPositionsLoading(true);
            }

            const now = new Date();
            const startDate = Base.dateStartOf(now.addDays(-1), "month").format(
                "YYYY-MM-DD"
            );
            const endDate = Base.dateEndOf(now, "month").format("YYYY-MM-DD");

            // Get current workshift for given employeeID's with workshift timeslots
            getWorkShiftsNew(visibleEmployeeIds, startDate, endDate, true)
                .then((res) => {
                    const filteredWorkShifts =
                        res?.data
                            ?.filter(
                                (shift) =>
                                    shift.endTime === null ||
                                    shift.workShiftTimeSlots?.some(
                                        (slot) => slot.endDate === null
                                    )
                            )
                            ?.map((shift) => ({
                                ...shift,
                                workShiftTimeSlots:
                                    shift.workShiftTimeSlots.filter(
                                        (slot) => slot.endDate === null
                                    ),
                            })) || [];

                    setOngoingWorkShifts(filteredWorkShifts);
                    if (showLoadingIndicator) {
                        setPositionsLoading(false);
                    }
                })
                .catch((e) => {
                    if (showLoadingIndicator) {
                        setPositionsLoading(false);
                    }
                    handleApiError(e, dispatch);
                });
        },
        [visibleEmployeeIds]
    );

    useEffect(() => {
        if (!workTimeTypes) {
            dispatch(fetchWorkTimeTypes() as any);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Automatically toggle all filters by default when workTimeTypes are loaded
    useEffect(() => {
        if (workTimeTypes && !hasInitializedFilters.current) {
            const allWorkTimeTypeIds = workTimeTypes.map(
                (type) => type.workTimeTypeId
            );
            dispatch(setWorkTimeTypeIds(allWorkTimeTypeIds));
            hasInitializedFilters.current = true;
        }
    }, [dispatch, workTimeTypes]);

    useEffect(() => {
        fetchOngoingWorkShifts();
    }, [fetchOngoingWorkShifts]);

    useFetch(
        (signal) => getVehicles(false, signal),
        {
            onSuccess: (res) => void dispatch(setVehicles(res.data)),
            withLoadingIndicator: false,
        },
        []
    );

    useEffect(() => {
        if (allVehicleGroups.length === 0) {
            dispatch(fetchAllVehicleGroups([]) as any);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!vehicleCameras) {
            dispatch(fetchAllVehicleCameras() as any);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!!positions) setPositionsLoading(false);
    }, [positions]);

    useEffect(() => {
        if (!!selectedWorkShift) setWorkShiftLoading(false);
    }, [selectedWorkShift]);

    useEffect(() => {
        if (!!ongoingWorkShifts) setWorkShiftsLoading(false);
    }, [ongoingWorkShifts]);

    const fetchLocations = useCallback(
        (showLoadingIndicator = false) => {
            if (!selectedVehicleId && vehicles?.length > 0) {
                if (showLoadingIndicator) {
                    setPositionsLoading(true);
                }

                getVehiclePositions(
                    selectedVehicleId
                        ? [selectedVehicleId]
                        : vehicles.map((v) => v.id),
                    true
                )
                    .then((res) => {
                        dispatch(setPositions(res?.data));
                    })
                    .catch((e) => {
                        if (showLoadingIndicator) {
                            setPositionsLoading(false);
                        }
                        handleApiError(e, dispatch);
                    });
            }
        },
        [vehicles, selectedVehicleId]
    );

    // Fetch workshift and workshift timeslot for employeeId that is also found in positions for the vehicle
    const fetchWorkShift = useCallback(
        (showLoadingIndicator = false) => {
            if (!!selectedEmployeeId) {
                if (showLoadingIndicator) {
                    setWorkShiftLoading(true);
                }

                getCurrentWorkShift(selectedEmployeeId, true)
                    .then((res) => dispatch(setSelectedWorkShift(res)))
                    .catch((e) => {
                        if (showLoadingIndicator) {
                            setWorkShiftLoading(false);
                        }
                        handleApiError(e, dispatch);
                    });
            }
        },
        [selectedEmployeeId]
    );

    const fetchEmployees = useCallback(() => {
        if (employees.length === 0) {
            dispatch(getEmployees() as any);
        }
    }, [employees]);

    const fetchActiveTransportOrders = useCallback(() => {
        getTransportOrders({
            selectedOrderStates: [
                TransportOrderState.OrderCreated,
                TransportOrderState.PickedUp,
            ],
            pageSize: 100,
        })
            .then((res) => {
                setTransportOrders(res.data?.items ?? []);
            })
            .catch((e) => handleApiError(e, dispatch));
    }, [dispatch]);

    useEffect(() => {
        fetchActiveTransportOrders();
    }, [fetchActiveTransportOrders]);

    const onRefresh = (showLoadingIndicator = false) => {
        fetchLocations(showLoadingIndicator);
        fetchWorkShift(showLoadingIndicator);
        fetchActiveTransportOrders();
        reload();
    };

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    useEffect(() => {
        fetchWorkShift();
    }, [fetchWorkShift]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    useInterval(onRefresh, refreshInterval);

    return (
        <MonitorViewMainLayout
            positions={positions}
            loading={loading}
            workShifts={ongoingWorkShifts}
            transportOrders={transportOrders}
            reloadKey={key}
            onRefresh={onRefresh}
        />
    );
};
